/**
 * lib/incomingInspectionPdf.js
 * Generates a formatted, center-aligned PDF of an Incoming/In-Process
 * Inspection report — ported 1:1 from the standalone tool's
 * assets/js/pdf-export.js (same layout, same content), swapping the CDN
 * global `window.jspdf` for the npm `jspdf` package.
 */
import { jsPDF } from "jspdf";
import { computeRowLimits } from "@/lib/incomingInspectionTolerance";

/** Loads a data URI into an <img>, redraws it onto a canvas, and returns a
 *  normalized PNG data URI + natural dimensions — sidesteps jsPDF's
 *  inconsistent support for JPEG/WEBP/GIF input formats. */
function normalizeImageToPngDataUri(dataUri) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve({ dataUri: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => reject(new Error('Could not load the attached image.'));
    img.src = dataUri;
  });
}

function resolveReportImages(payload) {
  const { meta, images } = payload;
  if (Array.isArray(images) && images.length) return images;
  if (Array.isArray(meta?.images_data) && meta.images_data.length) return meta.images_data;
  const raw = meta?.image_data;
  if (!raw) return [];
  const s = String(raw).trim();
  if (s.startsWith('[')) {
    try { const arr = JSON.parse(s); return Array.isArray(arr) ? arr.filter(Boolean) : []; }
    catch (e) { return []; }
  }
  if (s.startsWith('data:image')) return [s];
  return [];
}

export async function exportReportToPDF(payload) {
  const { meta, rows, qc } = payload;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const pageW = doc.internal.pageSize.getWidth();
  const centerX = pageW / 2;
  let y = 50;

  // ---------- Header ----------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 41);
  doc.text(meta.title || 'Untitled Report', centerX, y, { align: 'center' });

  y += 14;
  doc.setDrawColor(47, 95, 224);
  doc.setLineWidth(1.4);
  doc.line(centerX - 90, y, centerX + 90, y);

  // ---------- Meta info grid ----------
  y += 30;
  doc.setFontSize(10);
  const metaRows = [
    ['Process', meta.process_name || '—', 'Part Number', meta.part_number || '—'],
    ['Part Description', meta.part_description || '—', 'Inspector', meta.operator_name || '—'],
    ['Shift Code', meta.shift_code || '—', 'Revision of Drawing', meta.drawing_revision || '—'],
    ['Sample Size (n)', String(meta.subgroup_size ?? '—'), 'Date', meta.created_at || new Date().toLocaleString()],
    ['LOT Qty', meta.lot_qty || '—', 'Supplier Name', meta.supplier_name || '—'],
  ];
  const colW = 240;
  const labelW = 108;
  const valueMaxW = colW - labelW - 12;
  const startX = centerX - colW;
  const lineH = 12;
  metaRows.forEach(([l1, v1, l2, v2]) => {
    if (y > 740) { doc.addPage(); y = 50; }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    const lines1 = doc.splitTextToSize(String(v1), valueMaxW);
    const lines2 = doc.splitTextToSize(String(v2), valueMaxW);
    const rowLines = Math.max(lines1.length, lines2.length, 1);

    doc.setFont('helvetica', 'bold'); doc.setTextColor(90, 100, 130);
    doc.text(l1 + ':', startX, y);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 41);
    lines1.forEach((line, i) => doc.text(line, startX + labelW, y + i * lineH));

    doc.setFont('helvetica', 'bold'); doc.setTextColor(90, 100, 130);
    doc.text(l2 + ':', startX + colW, y);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 41);
    lines2.forEach((line, i) => doc.text(line, startX + colW + labelW, y + i * lineH));

    y += rowLines * lineH + 6;
  });
  y += 8;

  // ---------- Dimension Data Entry table ----------
  if (Array.isArray(rows) && rows.length) {
    if (y > 620) { doc.addPage(); y = 50; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 23, 41);
    doc.text('Dimension Data Entry', centerX, y, { align: 'center' });
    y += 16;

    const tableW = 480;
    const tableX = centerX - tableW / 2;
    const colWidths = { idx: 18, dim: 50, tol: 54, min: 38, max: 38, samples: 130, status: 48, ncr: 104 };
    const rowH = 20;
    const headerH = 20;

    function statusColor(status) {
      if (status === 'NC') return [220, 38, 38];
      if (status === 'ACCEPTED') return [22, 163, 74];
      return [120, 128, 150];
    }

    function drawTableHeader() {
      doc.setFillColor(248, 249, 253);
      doc.setDrawColor(227, 231, 242);
      doc.rect(tableX, y, tableW, headerH, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(90, 100, 130);
      let cx = tableX;
      const heads = [['#', colWidths.idx], ['Dimension', colWidths.dim], ['Tolerance', colWidths.tol], ['Min', colWidths.min], ['Max', colWidths.max], ['Sample Readings', colWidths.samples], ['Status', colWidths.status], ['NCR Report', colWidths.ncr]];
      heads.forEach(([label, w]) => {
        doc.text(label, cx + w / 2, y + 13, { align: 'center' });
        cx += w;
      });
      y += headerH;
    }

    const rowLimits = computeRowLimits;

    drawTableHeader();
    rows.forEach((row, i) => {
      if (y + rowH > 800) { doc.addPage(); y = 50; drawTableHeader(); }
      doc.setDrawColor(227, 231, 242);
      doc.rect(tableX, y, tableW, rowH);
      let cx = tableX;
      const samplesText = (row.samples || []).map((v) => Number(v).toString()).join(', ');
      const limits = rowLimits(row);
      const fmtLimit2 = (v) => (v === undefined || v === null ? '—' : v.toFixed(4).replace(/\.?0+$/, ''));
      const cells = [
        [String(i + 1), colWidths.idx],
        [row.dimension || '—', colWidths.dim],
        [row.tolerance || '—', colWidths.tol],
        [limits ? fmtLimit2(limits.min) : '—', colWidths.min],
        [limits ? fmtLimit2(limits.max) : '—', colWidths.max],
        [samplesText || '—', colWidths.samples],
      ];
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(15, 23, 41);
      cells.forEach(([text, w]) => {
        doc.text(String(text), cx + w / 2, y + 13, { align: 'center' });
        cx += w;
      });
      const [r, g, b] = statusColor(row.status);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(r, g, b);
      doc.text(row.status || '—', cx + colWidths.status / 2, y + 13, { align: 'center' });
      cx += colWidths.status;

      if (row.status === 'NC' && row.ncr) {
        const isClosed = row.ncr.status === 'CLOSED';
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
        doc.setTextColor(isClosed ? 22 : 220, isClosed ? 163 : 38, isClosed ? 74 : 38);
        const label = row.ncr.ncr_no + (row.ncr.is_complete ? (isClosed ? ' (Closed)' : ' (Open)') : ' (Draft)');
        doc.text(label, cx + colWidths.ncr / 2, y + 13, { align: 'center' });
      } else if (row.status === 'NC') {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(220, 38, 38);
        doc.text('Not yet raised', cx + colWidths.ncr / 2, y + 13, { align: 'center' });
      } else {
        doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 128, 150);
        doc.text('—', cx + colWidths.ncr / 2, y + 13, { align: 'center' });
      }
      y += rowH;
    });
    y += 26;
  }

  // ---------- Visual / Fitment / Surface Finish / Thread 1 / Thread 2 table ----------
  if (qc && (qc.visual || qc.fitment || qc.surface_finish || qc.thread1 || qc.thread2)) {
    if (y > 660) { doc.addPage(); y = 50; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 23, 41);
    doc.text('Visual, Fitment & Surface Finish', centerX, y, { align: 'center' });
    y += 16;

    const tableW = 460;
    const tableX = centerX - tableW / 2;
    const colWidths = { check: 100, remarks: 230, result: 60, ncr: 70 };
    const rowH = 22;

    doc.setFillColor(248, 249, 253);
    doc.setDrawColor(227, 231, 242);
    doc.rect(tableX, y, tableW, 20, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(90, 100, 130);
    let hx = tableX;
    [['Check', colWidths.check], ['Remarks', colWidths.remarks], ['Result', colWidths.result], ['NCR', colWidths.ncr]].forEach(([label, w]) => {
      doc.text(label, hx + w / 2, y + 13, { align: 'center' });
      hx += w;
    });
    y += 20;

    const qcRows = [
      ['Visual', qc.visual],
      ['Fitment', qc.fitment],
      ['Surface Finish', qc.surface_finish],
      ['Thread 1', qc.thread1],
      ['Thread 2', qc.thread2],
    ];
    qcRows.forEach(([label, entry]) => {
      if (!entry) return;
      if (y + rowH > 800) { doc.addPage(); y = 50; }
      doc.setDrawColor(227, 231, 242);
      doc.rect(tableX, y, tableW, rowH);
      let cx = tableX;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(15, 23, 41);
      doc.text(label, cx + colWidths.check / 2, y + 14, { align: 'center' });
      cx += colWidths.check;
      const remarksLines = doc.splitTextToSize(entry.remarks || '—', colWidths.remarks - 10);
      doc.text(remarksLines[0] || '—', cx + colWidths.remarks / 2, y + 14, { align: 'center' });
      cx += colWidths.remarks;
      const isNc = entry.result === 'NC';
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(isNc ? 220 : 22, isNc ? 38 : 163, isNc ? 38 : 74);
      doc.text(entry.result || 'ACCEPTED', cx + colWidths.result / 2, y + 14, { align: 'center' });
      cx += colWidths.result;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
      if (isNc && entry.ncr) {
        doc.setTextColor(entry.ncr.status === 'CLOSED' ? 22 : 220, entry.ncr.status === 'CLOSED' ? 163 : 38, entry.ncr.status === 'CLOSED' ? 74 : 38);
        doc.text(entry.ncr.ncr_no || '—', cx + colWidths.ncr / 2, y + 14, { align: 'center' });
      } else if (isNc) {
        doc.setTextColor(220, 38, 38);
        doc.text('Not raised', cx + colWidths.ncr / 2, y + 14, { align: 'center' });
      } else {
        doc.setTextColor(120, 128, 150);
        doc.text('—', cx + colWidths.ncr / 2, y + 14, { align: 'center' });
      }
      y += rowH;
    });
    y += 26;
  }

  // ---------- Non-Conformance Reports (full detail appendix) ----------
  const ncrList = [];
  (rows || []).forEach((row) => { if (row.status === 'NC' && row.ncr && row.ncr.detail) ncrList.push(row.ncr.detail); });
  ['visual', 'fitment', 'surface_finish', 'thread1', 'thread2'].forEach((key) => {
    const entry = qc && qc[key];
    if (entry && entry.result === 'NC' && entry.ncr && entry.ncr.detail) ncrList.push(entry.ncr.detail);
  });
  if (ncrList.length) {
    doc.addPage(); y = 50;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(15, 23, 41);
    doc.text('Non-Conformance Reports', centerX, y, { align: 'center' });
    y += 22;

    ncrList.forEach((d) => {
      if (y > 700) { doc.addPage(); y = 50; }
      const fieldW = 460, fieldX = centerX - fieldW / 2;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(47, 95, 224);
      doc.text(`${d.ncr_no || 'NCR'} — ${d.status === 'CLOSED' ? 'Closed' : 'Open'}`, fieldX, y);
      y += 16;
      const line = (label, value) => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(90, 100, 130);
        doc.text(label + ':', fieldX, y);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 41);
        const lines = doc.splitTextToSize(String(value || '—'), fieldW - 150);
        lines.forEach((ln, i) => doc.text(ln, fieldX + 150, y + i * 12));
        y += Math.max(lines.length, 1) * 12 + 4;
      };
      line('Part Name / Number', `${d.part_name || '—'} / ${d.part_number || '—'}`);
      line('NC Raised By', d.raised_by_name);
      line('Non-conformance Area', d.nc_area);
      line('Non-conformance Details', d.nc_details);
      line('Rejection Qty', d.rejection_qty);
      line('Disposition Action Taken', d.disposition_action);
      line('Responsibility', d.responsibility);
      y += 8;
      if (y > 780) { doc.addPage(); y = 50; }
    });
  }

  // ---------- Notes ----------
  if (meta.notes) {
    if (y > 700) { doc.addPage(); y = 50; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(15, 23, 41);
    doc.text('Notes', centerX, y, { align: 'center' });
    y += 16;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(43, 53, 80);
    const lines = doc.splitTextToSize(meta.notes, 420);
    lines.forEach((line) => {
      doc.text(line, centerX, y, { align: 'center' });
      y += 13;
    });
  }

  // ---------- Attached process/part images ----------
  const reportImages = resolveReportImages({ meta, images: payload.images });
  if (reportImages.length) {
    if (y > 700) { doc.addPage(); y = 50; }
    y += 16;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 23, 41);
    doc.text('Process / Part Images', centerX, y, { align: 'center' });
    y += 16;

    const maxW = 240, maxH = 180;
    for (const dataUri of reportImages) {
      try {
        const normalized = await normalizeImageToPngDataUri(dataUri);
        let imgW = normalized.width, imgH = normalized.height;
        const scale = Math.min(maxW / imgW, maxH / imgH, 1);
        imgW *= scale; imgH *= scale;
        if (y + imgH > 800) { doc.addPage(); y = 50; }
        doc.addImage(normalized.dataUri, 'PNG', centerX - imgW / 2, y, imgW, imgH);
        y += imgH + 20;
      } catch (e) {
        // If one image fails to load/normalize, skip it rather than failing the whole PDF.
      }
    }
  }

  // ---------- Footer ----------
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(150, 155, 175);
    doc.text('Generated by Incoming/In-Process Inspection — Canares Automation', centerX, 812, { align: 'center' });
    doc.text(`Page ${p} of ${pageCount}`, centerX, 824, { align: 'center' });
  }

  const filename = (meta.title || 'SPC_Report').replace(/[^a-z0-9]+/gi, '_') + '.pdf';
  doc.save(filename);
}
